import { WatchStatus } from '@/types/application/WatcherDTO';
import useGetApi from 'src/api/useGetApi';
import { keepPreviousData } from '@tanstack/react-query';
import {
  SalesOrderDraftStatus,
  SalesOrderSource,
  LineItem,
} from 'src/types/application/SalesOrderDraftDTO';

export interface SalesOrderDraftPreview {
  id: string;
  organizationId: string;
  status: SalesOrderDraftStatus;
  source: string;
  sourceId: string;
  customerId?: string;
  suggestedCustomerId?: string;
  suggestedCustomer?: {
    id: string;
    name: string;
    address?: string;
    phoneNumber?: string;
    emailDomain?: string;
    contactPerson?: string;
    creditLimit?: number;
  };
  suggestedCustomerRejected: boolean;
  suggestedCustomerCreationRejected: boolean;
  lineItems?: LineItem[];
  purchaseOrderNumber?: string;
  receivedDate?: Date;
  orderDate?: Date;
  deliveryDate?: Date;
  totalAmount?: number;
  currency?: string;
  destinationAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  shipToLocationId?: string | null;
  shipToLocation?: {
    id: string;
    locationName: string;
    address?: {
      locality?: string;
      administrativeArea?: string;
    } | null;
  } | null;
  createdAt: Date;
  updatedAt: Date;
  originId?: string | null;
  originSystem?: string | null;
  salesOrderId?: string | null;
  /** True when AI suggested approve_order and all suggested actions have been applied */
  isReadyForApproval?: boolean;
}

interface PageInfo {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface QueryParams {
  page?: number;
  pageSize?: number;
  status?: SalesOrderDraftStatus | SalesOrderDraftStatus[];
  customerId?: string | string[];
  excludedCustomerId?: string | string[];
  source?: SalesOrderSource | SalesOrderSource[];
  watchStatus?: WatchStatus[];
  purchaseOrderNumber?: string;
  customerEbsOrgId?: number;
  /** Filter by isReadyForApproval computed field (AI-based) */
  isReadyForApproval?: boolean;
}

type ApiResponse = {
  edges: SalesOrderDraftPreview[];
  pageInfo: PageInfo;
};

const useSalesOrderDraftsV2 = (params?: QueryParams, enabled?: boolean) => {
  // Convert array params to comma-separated strings to align with backend expectations
  const normalisedParams = {
    ...params,
    ...(Array.isArray(params?.status)
      ? { status: (params?.status as SalesOrderDraftStatus[]).join(',') }
      : {}),
    ...(Array.isArray(params?.customerId)
      ? { customerId: (params?.customerId as string[]).join(',') }
      : {}),
    ...(Array.isArray(params?.excludedCustomerId)
      ? {
          excludedCustomerId: (params?.excludedCustomerId as string[]).join(
            ',',
          ),
        }
      : {}),
    ...(Array.isArray(params?.source)
      ? { source: (params?.source as SalesOrderSource[]).join(',') }
      : {}),
    ...(Array.isArray(params?.watchStatus)
      ? { watchStatus: (params?.watchStatus as WatchStatus[]).join(',') }
      : {}),
  };

  return useGetApi<ApiResponse>({
    endpoint: 'accounts-receivable/staged-purchase-orders-v2',
    queryParameters: normalisedParams,
    options: {
      enabled,
      placeholderData: keepPreviousData,
    },
  });
};

export default useSalesOrderDraftsV2;
